"use client";

import { useCallback, useState } from "react";
import { useStore } from "react-redux";
import type { iUser } from "@/@sdk/user-management/user-management.models";
import type { UserOnboardingStatusResponse } from "@/@sdk/user-management/user-management.controller-models";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { reloadCurrentUser } from "@/store/modules/users/users.helpers";
import { ExpressWizardShell } from "../shared/express-wizard-shell";
import { UserWelcomeStep } from "./steps/welcome-step";
import { UserDoneStep } from "./steps/done-step";
import { ProfileForm } from "@/components/my-profile-modal/profile-form";

const USER_WIZARD_STEPS = [
  { id: "welcome", label: "Welcome" },
  { id: "profile", label: "Profile" },
  { id: "done", label: "Done" },
] as const;

type UserWizardStepId = (typeof USER_WIZARD_STEPS)[number]["id"];

type ExpressUserOnboardingWizardProps = {
  open: boolean;
  user: iUser;
  status: UserOnboardingStatusResponse;
  initialStepId?: UserWizardStepId;
  onComplete: () => void;
};

export function ExpressUserOnboardingWizard({
  open,
  user,
  status,
  initialStepId = "welcome",
  onComplete,
}: ExpressUserOnboardingWizardProps) {
  const store = useStore();
  const [currentStepId, setCurrentStepId] =
    useState<UserWizardStepId>(initialStepId);

  const { doAction: patchOnboarding } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (body: { userOnboardingLastStepId?: string }) =>
        sdk.patchUserOnboarding(body),
    }),
    []
  );

  const persistStep = useCallback(
    async (stepId: UserWizardStepId) => {
      await patchOnboarding({ userOnboardingLastStepId: stepId });
    },
    [patchOnboarding]
  );

  const goToStep = useCallback(
    async (stepId: UserWizardStepId) => {
      setCurrentStepId(stepId);
      await persistStep(stepId);
    },
    [persistStep]
  );

  const handleProfileSaved = useCallback(async () => {
    await reloadCurrentUser(store);
    await goToStep("done");
  }, [goToStep, store]);

  const handleFinish = useCallback(async () => {
    await patchOnboarding({ userOnboardingCompleted: true });
    await reloadCurrentUser(store);
    onComplete();
  }, [onComplete, patchOnboarding, store]);

  const stepIndex = USER_WIZARD_STEPS.findIndex(
    (step) => step.id === currentStepId
  );
  const isLastStep = currentStepId === "done";

  const stepTitle =
    currentStepId === "welcome"
      ? "Welcome to TA Desk"
      : currentStepId === "profile"
        ? "Set up your profile"
        : "All done";

  const stepDescription =
    currentStepId === "welcome"
      ? "A quick introduction before you get started."
      : currentStepId === "profile"
        ? "Confirm how you appear to customers on quotes and bookings."
        : undefined;

  return (
    <ExpressWizardShell
      open={open}
      title={stepTitle}
      description={stepDescription}
      steps={[...USER_WIZARD_STEPS]}
      currentStepId={currentStepId}
      blockDismiss
      showBack={stepIndex > 0 && currentStepId !== "done"}
      isLastStep={isLastStep}
      onBack={() => {
        const prev = USER_WIZARD_STEPS[stepIndex - 1];
        if (prev) void goToStep(prev.id);
      }}
      onNext={() => {
        const next = USER_WIZARD_STEPS[stepIndex + 1];
        if (next) void goToStep(next.id);
      }}
      onFinish={() => void handleFinish()}
      nextLabel={currentStepId === "welcome" ? "Get started" : "Continue"}
      finishLabel="Start using TA Desk"
      hideFooter={false}
      hidePrimaryAction={currentStepId === "profile"}
    >
      {currentStepId === "welcome" ? (
        <UserWelcomeStep
          firstName={user.data?.firstName ?? status.profilePrefill.firstName}
          status={status}
        />
      ) : null}

      {currentStepId === "profile" ? (
        <ProfileForm
          user={user}
          mode="wizard"
          prefill={status.profilePrefill}
          onSaved={() => void handleProfileSaved()}
        />
      ) : null}

      {currentStepId === "done" ? <UserDoneStep /> : null}
    </ExpressWizardShell>
  );
}
